/**
 * Break a URL into its parts, read its query string, and put it back together.
 *
 * The parts come from the browser's own URL parser (the WHATWG standard), so they are
 * what a browser would do with the address. Everything else is derived here: the query
 * is read from the raw text (so order, repeats and malformed encoding are visible rather
 * than tidied away), internationalised host names are decoded from Punycode, and the
 * changes the parser made to the address are listed.
 */

export type HostKind = "domain" | "ipv4" | "ipv6" | "none";

export interface QueryParam {
  /** The text as it appears in the address. */
  rawName: string;
  rawValue: string;
  name: string;
  value: string;
  /** The text had a "=" (a bare `flag` has none). */
  hasEquals: boolean;
  /** A %-sequence that is not valid UTF-8: the raw text is shown instead. */
  malformed: boolean;
  /** A name used for tracking: utm_source, gclid, fbclid … */
  tracking: boolean;
  /** The name appears more than once. */
  repeated: boolean;
}

export interface PathSegment {
  raw: string;
  decoded: string;
}

export interface UrlParts {
  scheme: string;
  username: string;
  password: string;
  hostname: string;
  /** The host as people read it: Punycode labels turned back into Unicode. */
  unicodeHost: string;
  hostKind: HostKind;
  labels: string[];
  port: string;
  /** The port that applies: the written one, or the scheme's default. */
  effectivePort: string;
  portIsDefault: boolean;
  pathname: string;
  segments: PathSegment[];
  search: string;
  params: QueryParam[];
  hash: string;
  hashDecoded: string;
  /** Parameters read from a fragment such as `#a=1&b=2`; empty when it is not one. */
  hashParams: QueryParam[];
  origin: string;
  href: string;
  /** No scheme or host was written: a path or query on its own. */
  relative: boolean;
  /** The scheme was missing and https:// was assumed. */
  assumedScheme: boolean;
  notes: UrlNote[];
}

export type UrlNote =
  | "assumedScheme"
  | "relative"
  | "credentials"
  | "schemeLowercased"
  | "hostNormalised"
  | "portRemoved"
  | "pathAdded"
  | "dotSegments"
  | "reEncoded"
  | "backslashes"
  | "malformedEncoding"
  | "internationalHost"
  | "noHost";

export type UrlErrorCode =
  | "empty"
  | "badPort"
  | "badHost"
  | "badScheme"
  | "invalid";

export type UrlResult =
  | { ok: true; parts: UrlParts }
  | { ok: false; code: UrlErrorCode };

const DEFAULT_PORTS: Readonly<Record<string, string>> = {
  http: "80",
  https: "443",
  ftp: "21",
  ws: "80",
  wss: "443",
};

const TRACKING =
  /^(?:utm_[a-z_]+|gclid|gclsrc|dclid|fbclid|msclkid|mc_cid|mc_eid|igshid|yclid|_ga|_gl|vero_id)$/i;
const RE_SCHEME = /^[A-Za-z][A-Za-z0-9+.-]*:/;
const RE_AUTHORITY = /^[A-Za-z][A-Za-z0-9+.-]*:\/\/([^/?#\\]*)/;
const RE_IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
const RE_EXPLICIT_PORT = /:(\d+)$/;
const RE_HAS_SCHEME_SLASHES = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//;
const RE_HOST_LIKE = /^[^\s/?#]+\.[^\s/?#]+/;
const RE_HOST_PORT = /^(?:localhost|[^\s/?#:]+\.[^\s/?#:]+):\d+(?:[/?#]|$)/;
const RE_PERCENT = /%[0-9A-Fa-f]{2}/g;
const RE_DOT_SEGMENT = /(?:^|\/)\.{1,2}(?:\/|$)/;
const RE_LEADING_SLASH = /^\//;
const RE_LEADING_HASH = /^#/;
const RE_AUTHORITY_PART = /^[^/?#\\]*/;
const RE_QUERY_OR_HASH = /[?#]/;
const RE_NON_ASCII = /\P{ASCII}/u;

// ---------------------------------------------------------------------------
// Punycode (RFC 3492), decoding only
// ---------------------------------------------------------------------------

const BASE = 36;
const T_MIN = 1;
const T_MAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 128;

function adapt(delta: number, count: number, first: boolean): number {
  let d = first ? Math.floor(delta / DAMP) : Math.floor(delta / 2);
  d += Math.floor(d / count);
  let k = 0;
  while (d > Math.floor(((BASE - T_MIN) * T_MAX) / 2)) {
    d = Math.floor(d / (BASE - T_MIN));
    k += BASE;
  }
  return k + Math.floor(((BASE - T_MIN + 1) * d) / (d + SKEW));
}

function digitValue(code: number): number {
  if (code >= 48 && code <= 57) {
    return code - 22;
  }
  if (code >= 65 && code <= 90) {
    return code - 65;
  }
  return code >= 97 && code <= 122 ? code - 97 : BASE;
}

/** Decode one Punycode label (without the `xn--`), or null when it is malformed. */
function punycodeDecode(input: string): string | null {
  const basic = input.lastIndexOf("-");
  const output: number[] = [];
  for (let j = 0; j < Math.max(basic, 0); j++) {
    output.push(input.charCodeAt(j));
  }
  let n = INITIAL_N;
  let bias = INITIAL_BIAS;
  let i = 0;
  for (let index = basic > 0 ? basic + 1 : 0; index < input.length; ) {
    const oldI = i;
    let weight = 1;
    for (let k = BASE; ; k += BASE) {
      if (index >= input.length) {
        return null;
      }
      const digit = digitValue(input.charCodeAt(index++));
      if (digit >= BASE) {
        return null;
      }
      i += digit * weight;
      const threshold =
        k <= bias ? T_MIN : Math.min(Math.max(k - bias, T_MIN), T_MAX);
      if (digit < threshold) {
        break;
      }
      weight *= BASE - threshold;
    }
    const length = output.length + 1;
    bias = adapt(i - oldI, length, oldI === 0);
    n += Math.floor(i / length);
    i %= length;
    if (n > 0x10_ff_ff) {
      return null;
    }
    output.splice(i++, 0, n);
  }
  return String.fromCodePoint(...output);
}

/** A host name with its `xn--` labels decoded. */
export function toUnicodeHost(hostname: string): string {
  return hostname
    .split(".")
    .map((label) => {
      if (!label.toLowerCase().startsWith("xn--")) {
        return label;
      }
      return punycodeDecode(label.slice(4)) ?? label;
    })
    .join(".");
}

// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------

function decodeComponent(raw: string): { text: string; malformed: boolean } {
  const spaced = raw.replaceAll("+", " ");
  try {
    return { text: decodeURIComponent(spaced), malformed: false };
  } catch {
    return { text: spaced, malformed: true };
  }
}

/** Read `a=1&b=2` the way a form decoder would, keeping order, repeats and bare names. */
export function parseQuery(query: string): QueryParam[] {
  const body =
    query.startsWith("?") || query.startsWith("#") ? query.slice(1) : query;
  if (body === "") {
    return [];
  }
  const params: QueryParam[] = body
    .split("&")
    .filter((piece) => piece !== "")
    .map((piece) => {
      const equals = piece.indexOf("=");
      const rawName = equals < 0 ? piece : piece.slice(0, equals);
      const rawValue = equals < 0 ? "" : piece.slice(equals + 1);
      const name = decodeComponent(rawName);
      const value = decodeComponent(rawValue);
      return {
        rawName,
        rawValue,
        name: name.text,
        value: value.text,
        hasEquals: equals >= 0,
        malformed: name.malformed || value.malformed,
        tracking: TRACKING.test(name.text),
        repeated: false,
      };
    });
  const counts = new Map<string, number>();
  for (const p of params) {
    counts.set(p.name, (counts.get(p.name) ?? 0) + 1);
  }
  for (const p of params) {
    p.repeated = (counts.get(p.name) ?? 0) > 1;
  }
  return params;
}

/** Parameters as a JSON object: repeated names become arrays. */
export function paramsToObject(
  params: QueryParam[]
): Record<string, string | string[]> {
  const out = new Map<string, string | string[]>();
  for (const p of params) {
    const existing = out.get(p.name);
    if (existing === undefined) {
      out.set(p.name, p.value);
    } else if (Array.isArray(existing)) {
      existing.push(p.value);
    } else {
      out.set(p.name, [existing, p.value]);
    }
  }
  return Object.fromEntries(out);
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

function segmentsOf(pathname: string): PathSegment[] {
  if (pathname === "" || pathname === "/") {
    return [];
  }
  return pathname
    .replace(RE_LEADING_SLASH, "")
    .split("/")
    .map((raw) => ({
      raw,
      decoded: decodeComponent(raw.replaceAll("+", "%2B")).text,
    }));
}

function hostKindOf(hostname: string): HostKind {
  if (hostname === "") {
    return "none";
  }
  if (hostname.startsWith("[")) {
    return "ipv6";
  }
  return RE_IPV4.test(hostname) ? "ipv4" : "domain";
}

function errorCode(text: string): UrlErrorCode {
  if (!RE_SCHEME.test(text)) {
    return "badScheme";
  }
  const authority = RE_AUTHORITY.exec(text);
  if (!authority) {
    return "invalid";
  }
  const host = authority[1].slice(authority[1].lastIndexOf("@") + 1);
  const port = RE_EXPLICIT_PORT.exec(host);
  if (port && Number(port[1]) > 65_535) {
    return "badPort";
  }
  if (host === "" || host.startsWith(":")) {
    return "badHost";
  }
  return host.includes(":") && !host.startsWith("[") && !port
    ? "badPort"
    : "badHost";
}

function notesFor(
  original: string,
  url: URL,
  flags: { assumed: boolean; relative: boolean }
): UrlNote[] {
  const notes: UrlNote[] = [];
  if (flags.assumed) {
    notes.push("assumedScheme");
  }
  if (flags.relative) {
    notes.push("relative");
  }
  if (url.username !== "" || url.password !== "") {
    notes.push("credentials");
  }
  const schemeWritten = RE_SCHEME.exec(original)?.[0].slice(0, -1) ?? "";
  if (schemeWritten !== "" && schemeWritten !== schemeWritten.toLowerCase()) {
    notes.push("schemeLowercased");
  }
  const authority = RE_AUTHORITY.exec(original)?.[1] ?? "";
  const hostWritten = authority
    .slice(authority.lastIndexOf("@") + 1)
    .replace(RE_EXPLICIT_PORT, "");
  if (hostWritten !== "" && hostWritten !== url.hostname && !flags.relative) {
    notes.push("hostNormalised");
  }
  if (RE_NON_ASCII.test(hostWritten)) {
    notes.push("internationalHost");
  }
  const portWritten = RE_EXPLICIT_PORT.exec(authority)?.[1];
  if (portWritten !== undefined && url.port === "") {
    notes.push("portRemoved");
  }
  return notes;
}

function pathNotes(original: string, url: URL, relative: boolean): UrlNote[] {
  const notes: UrlNote[] = [];
  const afterAuthority = original
    .replace(RE_HAS_SCHEME_SLASHES, "")
    .replace(RE_AUTHORITY_PART, "");
  const pathWritten = afterAuthority.split(RE_QUERY_OR_HASH)[0];
  if (!relative && pathWritten === "" && url.pathname === "/") {
    notes.push("pathAdded");
  }
  if (RE_DOT_SEGMENT.test(pathWritten)) {
    notes.push("dotSegments");
  }
  if (original.includes("\\")) {
    notes.push("backslashes");
  }
  const encodedBefore = (original.match(RE_PERCENT) ?? []).length;
  const encodedAfter = (
    (url.pathname + url.search + url.hash).match(RE_PERCENT) ?? []
  ).length;
  if (encodedAfter > encodedBefore) {
    notes.push("reEncoded");
  }
  return notes;
}

function buildParts(
  original: string,
  url: URL,
  flags: { assumed: boolean; relative: boolean }
): UrlParts {
  const scheme = url.protocol.slice(0, -1);
  const hostname = flags.relative ? "" : url.hostname;
  const kind = hostKindOf(hostname);
  const defaultPort = flags.relative ? "" : (DEFAULT_PORTS[scheme] ?? "");
  const notes = [
    ...notesFor(original, url, flags),
    ...pathNotes(original, url, flags.relative),
  ];
  const params = parseQuery(url.search);
  if (params.some((p) => p.malformed)) {
    notes.push("malformedEncoding");
  }
  if (kind === "none" && !flags.relative) {
    notes.push("noHost");
  }
  const hash = url.hash;
  const queryOnly =
    flags.relative && (original.startsWith("?") || original.startsWith("#"));
  const pathname = queryOnly ? "" : url.pathname;
  const hashBody = hash.slice(1);
  const hashParams =
    hashBody.includes("=") && !hashBody.startsWith("/") ? parseQuery(hash) : [];
  return {
    scheme: flags.relative ? "" : scheme,
    username: url.username,
    password: url.password,
    hostname,
    unicodeHost: kind === "domain" ? toUnicodeHost(hostname) : hostname,
    hostKind: kind,
    labels: kind === "domain" ? toUnicodeHost(hostname).split(".") : [],
    port: url.port,
    effectivePort: url.port === "" ? defaultPort : url.port,
    portIsDefault: url.port === "" && defaultPort !== "",
    pathname,
    segments: segmentsOf(pathname),
    search: url.search,
    params,
    hash,
    hashDecoded: decodeComponent(hashBody.replaceAll("+", "%2B")).text,
    hashParams,
    origin: flags.relative || url.origin === "null" ? "" : url.origin,
    href: flags.relative ? `${pathname}${url.search}${url.hash}` : url.href,
    relative: flags.relative,
    assumedScheme: flags.assumed,
    notes: dedupe(notes),
  };
}

function dedupe(notes: UrlNote[]): UrlNote[] {
  return [...new Set(notes)];
}

const BASE_FOR_RELATIVE = "https://relative.invalid";

/** Parse a URL. A missing scheme can be assumed to be https; a bare path is read as relative. */
export function parseUrl(input: string, assumeHttps: boolean): UrlResult {
  const text = input.trim();
  if (text === "") {
    return { ok: false, code: "empty" };
  }
  const relative =
    text.startsWith("/") && !text.startsWith("//")
      ? true
      : text.startsWith("?") || text.startsWith("#");
  try {
    if (relative) {
      return {
        ok: true,
        parts: buildParts(text, new URL(text, BASE_FOR_RELATIVE), {
          assumed: false,
          relative: true,
        }),
      };
    }
    const schemeless = !RE_SCHEME.test(text) || RE_HOST_PORT.test(text);
    const needsScheme =
      assumeHttps &&
      schemeless &&
      (RE_HOST_LIKE.test(text) || RE_HOST_PORT.test(text));
    const protocolRelative = text.startsWith("//") && assumeHttps;
    let withScheme = text;
    if (needsScheme) {
      withScheme = `https://${text}`;
    } else if (protocolRelative) {
      withScheme = `https:${text}`;
    }
    return {
      ok: true,
      parts: buildParts(text, new URL(withScheme), {
        assumed: needsScheme || (text.startsWith("//") && assumeHttps),
        relative: false,
      }),
    };
  } catch {
    return { ok: false, code: errorCode(text) };
  }
}

// ---------------------------------------------------------------------------
// Rebuilding
// ---------------------------------------------------------------------------

export type SpaceStyle = "percent" | "plus";

export interface EditableUrl {
  scheme: string;
  username: string;
  password: string;
  host: string;
  port: string;
  path: string;
  params: { name: string; value: string; hasEquals: boolean }[];
  hash: string;
}

/** Encode a query name or value. */
export function encodeComponent(text: string, space: SpaceStyle): string {
  const encoded = encodeURIComponent(text);
  return space === "plus" ? encoded.replaceAll("%20", "+") : encoded;
}

/** Put the pieces back together. Names and values are encoded; the path is left as written. */
export function buildUrl(url: EditableUrl, space: SpaceStyle): string {
  const query = url.params
    .filter((p) => p.name !== "" || p.value !== "")
    .map((p) => {
      const name = encodeComponent(p.name, space);
      return p.hasEquals || p.value !== ""
        ? `${name}=${encodeComponent(p.value, space)}`
        : name;
    })
    .join("&");
  const userinfo =
    url.username === "" && url.password === ""
      ? ""
      : `${encodeURIComponent(url.username)}${url.password === "" ? "" : `:${encodeURIComponent(url.password)}`}@`;
  const authority =
    url.host === ""
      ? ""
      : `//${userinfo}${url.host}${url.port === "" ? "" : `:${url.port}`}`;
  const scheme = url.scheme === "" ? "" : `${url.scheme}:`;
  const path =
    url.path === "" || url.path.startsWith("/") || authority === ""
      ? url.path
      : `/${url.path}`;
  const hash =
    url.hash === "" ? "" : `#${url.hash.replace(RE_LEADING_HASH, "")}`;
  return `${scheme}${authority}${path}${query === "" ? "" : `?${query}`}${hash}`;
}

/** The editable pieces of a parsed URL. */
export function toEditable(parts: UrlParts): EditableUrl {
  return {
    scheme: parts.scheme,
    username: safeDecode(parts.username),
    password: safeDecode(parts.password),
    host: parts.relative ? "" : parts.unicodeHost,
    port: parts.port,
    path: parts.pathname,
    params: parts.params.map((p) => ({
      name: p.name,
      value: p.value,
      hasEquals: p.hasEquals,
    })),
    hash: parts.hash.replace(RE_LEADING_HASH, ""),
  };
}

function safeDecode(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

/** True when a parameter name is one of the common tracking parameters. */
export function isTrackingName(name: string): boolean {
  return TRACKING.test(name);
}
